import { useGT } from 'gt-react';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import type { UserPickerProps } from './userPickerProps';

/**
 * Searchable picker over the active users of the current organization.
 *
 * Unlike `UserAutocomplete`, which reads the REST members endpoint and so
 * needs `User:Read`, this one uses `core.users.listInOrg`, open to every
 * member of the organization — the right source for pickers that gate on
 * something else (e.g. Feedback:Update for "reporting for").
 */
export function OrgUserSelect({
  userId,
  onSelect,
  className,
  disabled,
  excludeUserIds,
  placeholder,
}: UserPickerProps) {
  const gt = useGT();
  const { data: users, isLoading } = useUsersInOrg();
  const excluded = new Set(excludeUserIds ?? []);
  const options = (users ?? []).filter((u) => !excluded.has(u.id));

  return (
    <Autocomplete
      value={userId || null}
      getValue={(user) => user.id}
      getLabel={(user) =>
        user.email ? `${user.name} (${user.email})` : user.name
      }
      items={options}
      onValueChange={(next) => onSelect(next ?? '')}
      deselectable
      labels={pickerLabels({
        placeholder:
          placeholder ??
          gt('Search users...', { context: 'org user picker placeholder' }),
        search: gt('Search options...', {
          context: 'search box placeholder inside a picker dropdown',
        }),
      })}
      disabled={disabled || isLoading}
      className={className}
    />
  );
}
